import loadable from '@loadable/component';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import Sticky from 'react-sticky-el';
import { Col, Container, Nav, Navbar, NavItem, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import AdStore from '../../../store/AdStore';
import CategoryStore from '../../../store/CategoryStore';
import ConfigStore from '../../../store/ConfigStore';
import CustomNavigationLinkStore from '../../../store/CustomNavigationLinkStore';
import MenuStore from '../../../store/MenuStore';
import SectionStore from '../../../store/SectionStore';
import TranslationStore from '../../../store/TranslationStore';
import UIStore from '../../../store/UIStore';
import CustomNavigationLinkParentType from '../../../types/CustomNavigationLinkParentType';
import LinkType from '../../../types/LinkType';
import Paths from '../../../types/Paths';
import ProductAddedToCardModalType from '../../../types/ProductAddedToCartModalStyle';
import SectionIntegrationType from '../../../types/SectionIntegrationType';
import { addParametersToPath } from '../../../util/queryString';
import { transformInternalLink } from '../../../util/url';
import AccountLinks from '../../account/AccountLinks';
import MobileNavigationCart from '../../cart/MobileNavigationCart';
import NavigationCart from '../../cart/NavigationCart';
import NavigationHybridCart from '../../cart/NavigationHybridCart';
import ClickOutside from '../../common/ClickOutside';
import Icon from '../../common/Icon';
import NavigationMenuRow from '../../navigation/NavigationMenuRow';
import NavigationSectionSlider from '../../navigation/NavigationSectionSlider';
import NavigationSingleLevelMenuRow from '../../navigation/NavigationSingleLevelMenuRow';
import MobileNavigationProposal from '../../proposal/MobileNavigationProposal';
import NavigationProposal from '../../proposal/NavigationProposal';
import QuickSearch from '../../search/QuickSearch';
import LicensePlateInput from '../../vehicle/LicensePlateInput';
import CommonNavigationMiddle from '../CommonNavigationMiddle';
import NavigationLinkCarousel from '../NavigationLinkCarousel';
import NavigationLinkStatic from '../NavigationLinkStatic';
import NavigationLogo from '../NavigationLogo';
import NavigationNewsletterDropdown from '../NavigationNewsletterDropdown';
import NavigationPopover from '../NavigationPopover';
import NavigationRegionalSettingsMenu from '../NavigationRegionalSettingsMenu';
import NavigationTopLevelMegaMenu from '../NavigationTopLevelMegaMenu';
import NavigationWishListButton from '../NavigationWishListButton';
import TopNavigationSearchButton from '../TopNavigationSearchButton';
import useNavigationEntityConverter from '../../../hooks/navigation/useNavigationEntityConverter';
import NavigationCartPopover from '../../cart/NavigationCartPopover';

const MobileNavigation = loadable(() =>
  import(/* webpackChunkName: "navigation" */ '../MobileNavigation')
);

/**
 * TODO: Convert class component to function component for hooks
 */
@observer
export class CommonNavigation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSticky: false,
      showSearchOnMobile: false,
      showSearchOnDesktop: false,
      inputHasFocus: false,
    };

    this.originalMobileMenuHeight = null;
    this.stickyElement = null;
    this.headerTimeout = null;
    this.mobileMenuHeightCounted = false;
    this.fullHeaderElement = createRef();
  }

  componentDidMount() {
    const { uiStore } = this.props;
    const { showSearchOnDesktop, showSearchOnMobile } = this.props;
    if (!uiStore.isDesktop) {
      // Update header height on page load only if we're on mobile.
      this.updateHeaderHeight();
    }

    // Hide search field when the navbar is hidden on mobile
    autorun(
      () => {
        if (
          this.shouldHideSticky() &&
          showSearchOnMobile &&
          showSearchOnDesktop
        ) {
          this.setState(
            { showSearchOnMobile: false, showSearchOnDesktop: false },
            () => {
              this.updateHeaderHeight();
              this.blurElement();
            }
          );
        }
      },
      { delay: 500 }
    );

    // Attach resize event listener to window since ReactResizeDetector component reports resize event too early from landscape to portrait.
    !uiStore.isDesktop && !window.isSSR && this.attachResizeEventListener();
  }

  componentDidUpdate(_, prevState) {
    const { uiStore } = this.props;
    const { inputHasFocus, isSticky } = this.state;
    if (!uiStore.isDesktop && prevState.inputHasFocus !== inputHasFocus) {
      inputHasFocus && uiStore.toggleSearch(uiStore.isLandscape);
      !inputHasFocus && uiStore.toggleSearch(false);
      this.updateHeaderHeight();
    }

    if (prevState.isSticky !== isSticky) {
      this.updateHeaderHeight();
    }
  }

  componentWillUnmount() {
    const { uiStore } = this.props;
    if (this.headerTimeout) {
      clearTimeout(this.headerTimeout);
    }

    !uiStore.isDesktop &&
      !window.isSSR &&
      window.removeEventListener('resize', this.resizeHandler);
  }

  attachResizeEventListener = () => {
    window.addEventListener('resize', this.resizeHandler, false);
  };

  resizeHandler = () => {
    const { uiStore } = this.props;
    const { showSearchOnMobile, inputHasFocus } = this.state;
    if (!showSearchOnMobile || uiStore.isDesktop) {
      this.maybeResetSearchVisibilityState();
      this.updateHeaderHeight();
    }

    inputHasFocus && uiStore.toggleSearch(uiStore.isLandscape);
    this.updateHeaderHeight();
  };

  maybeResetSearchVisibilityState = () => {
    if (this.props.uiStore.isDesktop) {
      this.setState({
        showSearchOnMobile: false,
      });
    } else {
      this.setState({
        showSearchOnDesktop: false,
      });
    }
  };

  updateHeaderHeight = () => {
    const { uiStore } = this.props;

    if (this.stickyElement) {
      const currentHeaderHeight = this.stickyElement.wrapperEl.clientHeight;
      uiStore.setHeaderHeight(currentHeaderHeight);
    }
  };

  handleVehicleSearch = (licensePlate) => {
    const { routeService, history } = this.props;
    const licensePlateUrl = licensePlate
      ? addParametersToPath(Paths.VehiclePartSearch, {
          id: licensePlate,
        })
      : Paths.VehiclePartSearch;
    const url = routeService.getPath(licensePlateUrl);

    history.push(url);
  };

  onKeyPress = () => {
    this.blurElement();
  };

  onInputFocus = () => {
    this.setState({ inputHasFocus: true });
  };

  onInputBlur = () => {
    this.blurElement();
  };

  shouldShowFullHeader = () => {
    const { uiStore } = this.props;

    return (
      uiStore.isDesktop &&
      !this.searchIsFocused() &&
      !uiStore.scrollingDown &&
      uiStore.documentScrollTop <= uiStore.headerHeight &&
      uiStore.documentScrollTop !== 0 &&
      !this.shouldShowMobileMenu() &&
      !this.shouldShowMobileModal()
    );
  };

  shouldHideSticky = () => {
    const { uiStore, configStore } = this.props;
    const offset = uiStore.isDesktop ? 300 : 0;

    return (
      (!uiStore.isDesktop ||
        configStore.topNavigation.hideHeaderOnScrollingDown) &&
      !this.searchIsFocused() &&
      uiStore.scrollingDown &&
      uiStore.documentScrollTop > uiStore.headerHeight + offset &&
      !this.shouldShowMobileMenu() &&
      !this.shouldShowDesktopCanvas() &&
      !this.shouldShowMobileModal()
    );
  };

  shouldHideMixedContent = () => {
    const { configStore, sectionStore } = this.props;

    return (
      configStore.account.isMoneyTransfer ||
      (configStore.integrations.zoovu.enabled &&
        sectionStore.activeSection &&
        sectionStore.activeSection.integration_type ===
          SectionIntegrationType.ZOOVU)
    );
  };

  searchIsFocused = () => this.state.inputHasFocus;

  shouldShowMobileMenu = () => {
    const { uiStore } = this.props;
    return !uiStore.isDesktop && uiStore.mobileNavigation.isOpen;
  };

  shouldShowDesktopCanvas = () => {
    const { uiStore } = this.props;
    return uiStore.isDesktop && uiStore.navigationOffCanvas.isOpen;
  };

  shouldShowMobileModal = () => this.props.uiStore.popoverActive;

  onFixedToggle = (wasSticky) => this.setState({ isSticky: !wasSticky });

  getAdditionalLinks = () => {
    const { routeService, accountStore, configStore } = this.props;
    const additionalLinks = [];

    if (accountStore.isRetailer) {
      additionalLinks.push({
        id: '_customerProducts',
        label: (
          <FormattedMessage {...globalTranslations.customerProductsTitle} />
        ),
        path: routeService.getPath(Paths.CustomerProducts),
      });
    }

    if (configStore.navigation.sidebar.showOnSale) {
      additionalLinks.push({
        id: '_onSale',
        label: <FormattedMessage {...globalTranslations.onSaleTitle} />,
        path: routeService.getPath(Paths.OnSale),
      });
    }
    if (configStore.navigation.sidebar.showNewProducts) {
      additionalLinks.push({
        id: '_newProducts',
        label: <FormattedMessage {...globalTranslations.newProductsTitle} />,
        path: routeService.getPath(Paths.NewProducts),
      });
    }

    if (configStore.navigation.sidebar.showSoldProducts) {
      additionalLinks.push({
        id: '_soldProducts',
        label: <FormattedMessage {...globalTranslations.soldProductsTitle} />,
        path: routeService.getPath(Paths.SoldProducts),
      });
    }

    if (configStore.navigation.sidebar.showMostSold) {
      additionalLinks.push({
        id: '_mostSold',
        label: <FormattedMessage {...globalTranslations.mostPopularTitle} />,
        path: routeService.getPath(Paths.PopularProducts),
      });
    }

    if (configStore.navigation.sidebar.showRecommended) {
      additionalLinks.push({
        id: '_recommended',
        label: <FormattedMessage {...globalTranslations.weRecommendTitle} />,
        path: routeService.getPath(Paths.RecommendedProducts),
      });
    }

    return additionalLinks;
  };

  getBottomBarLinks = () => {
    const { sectionStore, categoryStore, uiStore, configStore } = this.props;

    let items;
    const closeMegaMenu = () =>
      uiStore.isDesktop ? uiStore.megaMenu.close() : null;

    if (configStore.activateSections) {
      items = sectionStore.activeSections.map((section) => {
        const toggleSection =
          section.mainCategoryIds && section.mainCategoryIds.length > 0
            ? () => uiStore.megaMenu.toggle(section)
            : () => uiStore.megaMenu.close();

        const onMouseOver = () =>
          uiStore.isDesktop ? this.debouncedHover(toggleSection) : null;
        const onMouseOut = () =>
          uiStore.isDesktop ? this.debouncedHover(closeMegaMenu) : null;

        const path = this.getCategoryOrSectionPath(section);
        return {
          id: section.id,
          label: (
            <span
              className="mouseClass"
              onClick={closeMegaMenu}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            >
              {section.navigationName}
            </span>
          ),
          path,
          className: classNames({
            open:
              uiStore.megaMenu.activeSection &&
              uiStore.megaMenu.activeSection.id === section.id,
          }),
          active: sectionStore.activeSection === section,
          children: [],
          type: section.link_type,
          customNavLinks: this.getCustomNavigationLinks(
            section,
            CustomNavigationLinkParentType.SECTION
          ),
        };
      });
    } else {
      items = categoryStore.categories.map((category) => {
        const toggleCategory =
          category.children && category.children.length > 0
            ? () => uiStore.megaMenu.toggle(category)
            : () => uiStore.megaMenu.close();

        const onMouseOver = () =>
          uiStore.isDesktop ? this.debouncedHover(toggleCategory) : null;
        const onMouseOut = () =>
          uiStore.isDesktop ? this.debouncedHover(closeMegaMenu) : null;

        const activeMainCategory =
          categoryStore.activeCategory &&
          categoryStore.activeCategory.mainCategory;

        const path = this.getCategoryOrSectionPath(category);
        return {
          id: category.id,
          label: (
            <span
              onClick={closeMegaMenu}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            >
              {category.navigationName}
            </span>
          ),
          path,
          className: classNames({
            open:
              uiStore.megaMenu.rootCategory &&
              uiStore.megaMenu.rootCategory.id === category.id,
          }),
          active: activeMainCategory === category,
          children: [],
          type: category.link_type,
          customNavLinks: this.getCustomNavigationLinks(
            category,
            CustomNavigationLinkParentType.CATEGORY
          ),
        };
      });
    }

    let additionalLinks = this.getAdditionalLinks();

    if (configStore.navigation.megaMenu.groupAdditionalLinks) {
      additionalLinks = this.groupAdditionalLinks(additionalLinks);
    }

    return items.concat(additionalLinks);
  };

  getCategoryOrSectionPath = (categoryOrSection) => {
    const { routeService } = this.props;

    if (categoryOrSection.link_type === LinkType.EXTERNAL_LINK) {
      return categoryOrSection.path;
    } else if (categoryOrSection.link_type === LinkType.INTERNAL_LINK) {
      return routeService.getPath(
        transformInternalLink(categoryOrSection.path)
      );
    } else {
      return routeService.getPath(categoryOrSection.path);
    }
  };

  getCustomNavigationLinks = (categoryOrSection, type) => {
    const { customNavigationLinkStore } = this.props;

    return type === CustomNavigationLinkParentType.CATEGORY
      ? customNavigationLinkStore.filterByCategoryId(categoryOrSection.id)
      : customNavigationLinkStore.filterBySectionId(categoryOrSection.id);
  };

  groupAdditionalLinks = (links) => {
    // No need to group, if only one item.
    if (links.length <= 1) {
      return links;
    }

    return [
      {
        id: '_customerProducts',
        label: (
          <FormattedMessage {...globalTranslations.additionalLinksTitle} />
        ),
        children: links,
        dropdown: true,
      },
    ];
  };

  getMenuBarLinks = () => {
    const { menuStore, routeService } = this.props;

    const items = useNavigationEntityConverter({ entities: menuStore.menus });

    items.unshift({
      id: '_top',
      path: routeService.getPath(Paths.FrontPage),
      type: LinkType.INTERNAL_LINK,
      image: null,
      label: (
        <>
          <Icon name="home" />
          <FormattedMessage {...globalTranslations.homeTitle} />
        </>
      ),
    });

    return items;
  };

  toggleSearch = () => {
    this.updateState('showSearchOnMobile');
    this.updateState('showSearchOnDesktop');
  };

  updateState = (stateVariable) => {
    this.setState(
      (prevState) => ({
        [stateVariable]: !prevState[stateVariable],
      }),
      () => this.focusElement()
    );
  };

  focusElement = () => {
    const { showSearchOnMobile, showSearchOnDesktop } = this.state;
    if ((showSearchOnMobile, showSearchOnDesktop)) {
      const element = this.getAutosuggestInput();

      if (element) {
        element.focus();
      }
    }
  };

  blurElement = () => {
    const element = this.getAutosuggestInput();

    if (element) {
      element.blur();
    }

    this.setState({
      inputHasFocus: false,
    });
  };

  // Could be worth to explore using useCallback and useEffect for more efficient debouncing.
  // This debounced function allows us to have a single timeout for all hover
  // (onMouseOut and onMouseOver) callbacks so that only the last one is called.
  //
  // This allows us to
  // 1) prevent showing MegaMenu when the user hovers over the menubar quickly
  // 2) prevent hiding MegaMenu when the user moves the mouse from the menubar
  //    to the actual MegaMenu. This is achieved by having a separate onMouseOver
  //    in the MegaMenu that overrides the last onMouseOut callback.
  //
  debouncedHover = debounce((callback) => {
    if (callback) {
      callback();
    }
  }, 200);

  getNavigationCart = () => {
    const { accountStore, configStore, uiStore } = this.props;
    const { isSticky } = this.state;

    const moveShoppingCartLoginRight =
      configStore.topNavigation.moveShoppingCartLoginRight;
    const doNotRenderCart =
      accountStore.isViewOnly || (moveShoppingCartLoginRight && isSticky);

    if (doNotRenderCart) {
      return null;
    }

    if (configStore.siteConfig.isHybrid) {
      return (
        <NavigationHybridCart
          navigationCart={<NavigationCart isText={true} />}
          navigationProposal={<NavigationProposal isText={true} />}
        />
      );
    }

    const shouldUseSimple =
      uiStore.isMobile && configStore.cart.button.simplifiedMobile;
    const navigationCart = shouldUseSimple ? (
      <MobileNavigationCart />
    ) : (
      <NavigationCart />
    );

    const navigationProposal = shouldUseSimple ? (
      <MobileNavigationProposal />
    ) : (
      <NavigationProposal />
    );

    return (
      <div className="NavigationCart__wrapper">
        {!configStore.siteConfig.proposalEnabled && navigationCart}
        {configStore.siteConfig.proposalEnabled && navigationProposal}
        {this.cartIsPopover() && <NavigationCartPopover />}
      </div>
    );
  };

  cartIsPopover = () => {
    const { accountStore, configStore } = this.props;

    if (!accountStore.account) {
      return null;
    }

    const isRetailer = accountStore.account.is_retailer;
    const modalType = configStore.cart.type;

    return isRetailer || modalType === ProductAddedToCardModalType.POPOVER;
  };

  renderNavigationTop = () => {
    const { configStore } = this.props;

    const moveShoppingCartLoginRight =
      configStore.topNavigation.moveShoppingCartLoginRight;

    const topRightNavigation = (
      <Nav className="CommonNavigation__top-right-navigation">
        {configStore.topNavigation.moveSearchbarTopNavigation && (
          <TopNavigationSearchButton
            onShowSearchOnTopNavigation={this.toggleSearch}
          />
        )}
        <NavigationNewsletterDropdown />
        <NavigationRegionalSettingsMenu />
        {configStore.wishlist.enabled && (
          <NavItem>
            <NavigationWishListButton />
          </NavItem>
        )}
        {configStore.siteConfig.isWebStore && (
          <NavItem>
            <AccountLinks />
          </NavItem>
        )}
      </Nav>
    );

    return (
      <Navbar className="CommonNavigation__top d-none d-lg-block">
        <Container className="CommonNavigation__top-container">
          <div className="CommonNavigation__top-inner">
            <NavigationMenuRow
              items={this.getMenuBarLinks()}
              isNoFollowLink={true}
              surface="top"
            />
            {moveShoppingCartLoginRight ? (
              <div className="CommonNavigation__top-right-navigation-shopping-buttons-wrapper">
                {topRightNavigation}
                <div className="CommonNavigation__top-right-cart-wrapper">
                  {this.getNavigationCart()}
                </div>
              </div>
            ) : (
              topRightNavigation
            )}
          </div>
          {configStore.topNavigation.moveSearchbarTopNavigation &&
            this.state.showSearchOnDesktop && (
              <Row className="justify-content-center pb-2">
                {configStore.vehiclePartSearch.enabled && (
                  <div>
                    <LicensePlateInput
                      handleOnSubmit={this.handleVehicleSearch}
                      clearAfterSubmit
                    />
                  </div>
                )}
                <Col sm="5">
                  <QuickSearch
                    handleFocus={this.onInputFocus}
                    handleBlur={this.onInputBlur}
                    toggleSearch={() => null}
                  />
                </Col>
              </Row>
            )}
        </Container>
      </Navbar>
    );
  };

  renderNavigationTopBalance = () => {
    return (
      <Navbar className="CommonNavigation__top d-none d-lg-block">
        <Container className="CommonNavigation__top-container">
          <div className="CommonNavigation__top-inner">
            <NavigationMenuRow
              items={this.getMenuBarLinks()}
              isNoFollowLink={true}
              surface="top"
            />
            <AccountLinks />
          </div>
        </Container>
      </Navbar>
    );
  };

  renderNavigationLogo = (ifShouldShowFullHeader) => {
    const { configStore, uiStore } = this.props;
    const { isSticky } = this.state;

    const useStickyHeaderOnStartup =
      uiStore.isDesktop && configStore.topNavigation.useStickyHeaderOnStartup;

    const vehiclePartSearchEnabled = configStore.vehiclePartSearch.enabled;

    if (vehiclePartSearchEnabled) {
      return (
        <NavigationLogo
          sticky={isSticky}
          useStickyHeaderOnStartup={useStickyHeaderOnStartup}
        />
      );
    }

    return (
      <NavigationLogo
        sticky={isSticky}
        useStickyHeaderOnStartup={useStickyHeaderOnStartup}
        ifShouldShowFullHeader={ifShouldShowFullHeader}
      />
    );
  };

  renderNavigationMiddle = () => {
    const { isSticky, showSearchOnMobile, inputHasFocus } = this.state;

    const ifShouldShowFullHeader = this.shouldShowFullHeader();

    return (
      <Navbar className="CommonNavigation__middle" expand="lg">
        <Container className="CommonNavigation__middle-container">
          <CommonNavigationMiddle
            isSticky={isSticky}
            onShowSearchOnMobile={this.toggleSearch}
            onSearchKeyPress={this.onKeyPress}
            inputFocus={this.onInputFocus}
            inputBlur={this.onInputBlur}
            inputHasFocus={inputHasFocus}
            shouldShowSearchOnMobile={showSearchOnMobile}
            ifShouldShowFullHeader={ifShouldShowFullHeader}
            navigationLogo={this.renderNavigationLogo(ifShouldShowFullHeader)}
          />
        </Container>
      </Navbar>
    );
  };

  renderNavigationBottom = () => {
    const { configStore } = this.props;

    if (
      this.shouldHideMixedContent() ||
      configStore.navigation.sidebar.enabled
    ) {
      return null;
    }

    return (
      <>
        <NavigationSingleLevelMenuRow
          items={this.getBottomBarLinks()}
          className="CommonNavigation__bottom"
        />
        {configStore.navigation.megaMenu.enabled && (
          <NavigationTopLevelMegaMenu debouncer={this.debouncedHover} />
        )}
      </>
    );
  };

  getAutosuggestInput = () => {
    return document.querySelector('.CommonNavigation .QuickSearch input');
  };

  renderNavigationPopover = () => {
    return <NavigationPopover navigationIsSticky={this.shouldHideSticky()} />;
  };

  handleMobileNavigationOpened = (mobileNavigationRef) => {
    if (this.fullHeaderElement?.current && mobileNavigationRef?.current) {
      const { uiStore } = this.props;
      const currentFullHeaderHeight =
        this.fullHeaderElement.current.offsetHeight;

      const currentMobileMenuHeight = mobileNavigationRef.current.offsetHeight;

      if (!this.originalMobileMenuHeight)
        this.originalMobileMenuHeight = currentMobileMenuHeight;

      uiStore.mobileNavigation.updateMenuHeight(
        window.innerHeight - currentFullHeaderHeight
      );
    }
  };

  handleMobileNavigationClosed = () => {
    this.props.uiStore.mobileNavigation.updateMenuHeight(null);
  };

  renderNavigationLinkAds = () => {
    const { adStore } = this.props;
    const adsCount = adStore.textBanners.length;

    if (adsCount === 0) {
      return null;
    }

    return adsCount === 1 ? (
      <NavigationLinkStatic />
    ) : (
      <NavigationLinkCarousel />
    );
  };

  render() {
    const { configStore, translationStore, uiStore } = this.props;
    const { isSticky } = this.state;
    const { mobileNavigation } = uiStore;

    const useStickyHeaderOnStartup =
      uiStore.isDesktop && configStore.topNavigation.useStickyHeaderOnStartup;
    const ifShouldShowFullHeader = this.shouldShowFullHeader();
    const moveShoppingCartLoginRight =
      configStore.topNavigation.moveShoppingCartLoginRight;

    const storeLogo = translationStore.storeLogo;
    return (
      <header
        className="CommonNavigation__full-header-background"
        ref={this.fullHeaderElement}
      >
        {this.renderNavigationLinkAds()}
        <div className="CommonNavigation__header-background">
          {uiStore.isDesktop && (
            <div
              className={classNames('CommonNavigation', {
                'CommonNavigation--show-full-header':
                  !useStickyHeaderOnStartup && !isSticky,
                'CommonNavigation--hide-sticky': this.shouldHideSticky(),
                'CommonNavigation--scroll-logo': useStickyHeaderOnStartup,
                'CommonNavigation--mobile-navigation-open':
                  this.shouldShowMobileMenu(),
                'CommonNavigation--forced-static': !!window.isSSR,
              })}
            >
              {!configStore.siteConfig.isHomePage && this.renderNavigationTop()}
              {configStore.siteConfig.isHomePage &&
                configStore.account.isMoneyTransfer &&
                this.renderNavigationTopBalance()}
            </div>
          )}
          <Sticky
            className={classNames('CommonNavigation', {
              'CommonNavigation--show-full-header':
                !useStickyHeaderOnStartup && ifShouldShowFullHeader,
              'CommonNavigation--hide-sticky': this.shouldHideSticky(),
              'CommonNavigation__full-header-background': isSticky,
              'CommonNavigation--scroll-logo': useStickyHeaderOnStartup,
              'CommonNavigation--mobile-navigation-open':
                this.shouldShowMobileMenu(),
              'CommonNavigation--forced-static': !!window.isSSR,
              'CommonNavigation--top-right-cart-enabled':
                uiStore.isDesktop && moveShoppingCartLoginRight,
            })}
            name="top"
            onFixedToggle={this.onFixedToggle}
            ref={(ref) => (this.stickyElement = ref)}
            positionRecheckInterval={10}
            holderProps={{
              className: 'CommonNavigation__sticky-placeholder',
            }}
          >
            {this.renderNavigationMiddle()}
            {configStore.siteConfig.isShoppingCenter && uiStore.isDesktop && (
              <div className="CommonNavigation__shopping-center--separator" />
            )}
            {!uiStore.isDesktop &&
              configStore.topNavigation.headerMobileSectionSlider && (
                <NavigationSectionSlider
                  items={this.getBottomBarLinks()}
                  className="CommonNavigation__bottom"
                />
              )}
            {this.shouldShowMobileMenu() && (
              <ClickOutside
                className="MobileNavigation__container"
                style={{ maxHeight: uiStore.mobileNavigation.mobileMenuHeight }}
                onClickOutside={mobileNavigation.toggle}
                excludedSelector=".MobileNavigationButton, .modal"
              >
                <MobileNavigation
                  shouldHideProductContent={this.shouldHideMixedContent()}
                  navigation={mobileNavigation}
                  onTabChange={this.handleMobileNavigationOpened}
                  onClose={this.handleMobileNavigationClosed}
                />
              </ClickOutside>
            )}
            {uiStore.isDesktop && this.renderNavigationBottom()}
            {uiStore.navigationPopover.contentId &&
              this.renderNavigationPopover()}
            {uiStore.isDesktop && isSticky && moveShoppingCartLoginRight && (
              <NavigationCartPopover />
            )}
          </Sticky>
        </div>
        {!uiStore.isDesktop &&
          configStore.mobileSettings.showStoreLogo &&
          storeLogo.length > 0 && (
            <Container className="CommonNavigation__mobile-logo-container">
              <div className="CommonNavigation__mobile-logo">
                <NavigationLogo />
              </div>
            </Container>
          )}
      </header>
    );
  }
}

CommonNavigation.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  adStore: modelOf(AdStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  menuStore: modelOf(MenuStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  translationStore: modelOf(TranslationStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  location: RouterPropTypes.location.isRequired,
};

export default inject(
  'accountStore',
  'adStore',
  'categoryStore',
  'configStore',
  'menuStore',
  'sectionStore',
  'translationStore',
  'uiStore',
  'customNavigationLinkStore',
  'routeService'
)(withRouter(CommonNavigation));
